import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useApi } from "@/hooks/use-api";
import { AuditAssessmentStats, AuditSuggestionStats } from "@/types/audit";
import { useEffect, useMemo, useState } from "react";
import {
  FileText,
  Inbox,
  ListCheck,
  ListEnd,
  ListX,
} from "lucide-react";
import { toast } from "sonner";
import { AuditCard } from "./AuditCard";
import { AuditTable } from "./AuditTable";
import { Separator } from "@/components/ui/separator";
import AppHeader from "@/components/AppHeader";

const emptySuggestions: AuditSuggestionStats = {
  approved: 0,
  rejected: 0,
  total: 0,
};

const aggregateAssessments = (assessments: AuditAssessmentStats[]) => {
  return assessments.reduce(
    (acc, assessment) => {
      acc.totalDocuments += assessment.totalDocuments || 0;
      acc.feedbackReviewed += assessment.feedbackReviewed || 0;
      acc.suggestions.approved += assessment.suggestions?.approved || 0;
      acc.suggestions.rejected += assessment.suggestions?.rejected || 0;
      acc.suggestions.total += assessment.suggestions?.total || 0;
      return acc;
    },
    {
      totalDocuments: 0,
      feedbackReviewed: 0,
      suggestions: { ...emptySuggestions },
    }
  );
};

export const Audits = () => {
  const { fetchAuditStats, exportAuditBundle } = useApi();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assessments, setAssessments] = useState<AuditAssessmentStats[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string>("all");
  const [downloadingAssessmentId, setDownloadingAssessmentId] = useState<
    string | null
  >(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchAuditStats();
        setAssessments(response.assessments || []);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to load audits";
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [fetchAuditStats]);

  const courseOptions = useMemo(() => {
    const values = new Set<string>();
    assessments.forEach((assessment) => {
      if (assessment.courseReference) {
        values.add(assessment.courseReference);
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [assessments]);

  const filteredAssessments = useMemo(() => {
    if (selectedCourse === "all") {
      return assessments;
    }
    return assessments.filter(
      (assessment) => assessment.courseReference === selectedCourse
    );
  }, [assessments, selectedCourse]);

  const totals = useMemo(
    () => aggregateAssessments(filteredAssessments),
    [filteredAssessments]
  );
  const ignoredSuggestions = Math.max(
    0,
    totals.suggestions.total -
    totals.suggestions.approved -
    totals.suggestions.rejected
  );

  const handleDownload = async (assessmentId: string) => {
    setDownloadingAssessmentId(assessmentId);
    try {
      await exportAuditBundle(assessmentId);
    } catch {
      toast.error("Failed to download audits ZIP");
    } finally {
      setDownloadingAssessmentId(null);
    }
  };

  return (
    <div className="h-full overflow-y-auto pt-10">
      <div className="mx-auto max-w-[1120px] px-10 py-8">
        <div className="mb-6 flex items-start justify-between gap-6">
          <div>
            <AppHeader primary="Audits" secondary="Overview" />
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Total assessments:</span>
              <Badge variant="secondary">
                {filteredAssessments.length}
              </Badge>
            </div>
          </div>

          <Select value={selectedCourse} onValueChange={setSelectedCourse}>
            <SelectTrigger className="w-[190px]">
              <SelectValue placeholder="All courses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All courses</SelectItem>
              {courseOptions.map((courseReference) => (
                <SelectItem key={courseReference} value={courseReference}>
                  {courseReference}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Separator className="mb-6"/>

        <div className="flex gap-10 py-4">
          <AuditCard
            title="Overview"
            description="Your activity at a glance."
            metrics={[
              {
                label: "Assessments",
                value: filteredAssessments.length,
                icon: <FileText className="h-4 w-4" />,
              },
              {
                label: "Submissions",
                value: totals.totalDocuments,
                icon: <Inbox className="h-4 w-4" />,
              },
            ]}
          />

          <AuditCard
            title="Suggestions"
            description="Your collaboration with Claire."
            metrics={[
              {
                label: "Approved",
                value: totals.suggestions.approved,
                icon: <ListCheck className="h-4 w-4" />,
                iconClassName: "text-green-600",
              },
              {
                label: "Rejected",
                value: totals.suggestions.rejected,
                icon: <ListX className="h-4 w-4" />,
                iconClassName: "text-red-600",
              },
              {
                label: "Ignored",
                value: ignoredSuggestions,
                icon: <ListEnd className="h-4 w-4" />,
              },
            ]}
          />
        </div>

        <Separator className="my-6"/>

        {isLoading && <div className="text-muted-foreground">Loading audits...</div>}
        {!isLoading && error && <div className="text-sm text-destructive">{error}</div>}

        {!isLoading && !error && (
          <AuditTable
            assessments={filteredAssessments}
            downloadingAssessmentId={downloadingAssessmentId}
            onDownload={handleDownload}
          />
        )}
      </div>
    </div>
  );
};
